import { Markdown } from '@apps/web/components/ui/markdown';
import { cn } from '@apps/web/lib/utils';
import { truncateArtifactText } from '@apps/web/routes/_app/admin/agent/artifacts/shared';
import { JsonPayloadViewer } from '@apps/web/routes/_app/admin/agent/json_payload_viewer';
import { stringifyPayload } from '@apps/web/routes/_app/admin/agent/payload_viewer_shared';
import {
    type AdminToolOutputVariant,
    isRecord,
    MAX_FILE_TABLE_ROWS,
    MAX_INLINE_MARKDOWN_CHARS,
    MAX_INLINE_TABLE_ROWS,
    MAX_JSON_STRING_LENGTH,
    MAX_JSON_TREE_CHILDREN,
    MAX_JSON_TREE_DEPTH,
    MAX_TABLE_COLUMNS,
} from '@apps/web/routes/_app/admin/agent/tool_output_renderer_shared';

export function TableRenderer({
    columns,
    rows,
    summaryLabel,
    variant,
}: {
    readonly columns: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string>>;
    readonly summaryLabel: string | null;
    readonly variant: AdminToolOutputVariant;
}) {
    const maxRows =
        variant === 'file' ? MAX_FILE_TABLE_ROWS : MAX_INLINE_TABLE_ROWS;
    const visibleColumns = columns.slice(0, MAX_TABLE_COLUMNS);
    const visibleRows = rows.slice(0, maxRows);

    return (
        <div
            data-testid="admin-tool-output-table"
            className="overflow-hidden rounded-[2px] border border-[var(--aa-border)] bg-white"
        >
            <div className="flex items-center justify-between border-b border-[var(--aa-border)] bg-[var(--aa-surface)] px-3 py-2">
                <span className="aa-mono aa-ink-soft text-[11px] font-medium">
                    Tabular result
                </span>
                {summaryLabel ? (
                    <span className="font-mono text-[10px] text-[var(--aa-ink-faint)] tabular-nums">
                        {summaryLabel}
                    </span>
                ) : null}
            </div>
            <div className="max-h-[30rem] overflow-auto">
                <table className="w-full text-left text-[12px]">
                    <thead className="sticky top-0 bg-[var(--aa-surface-sunk)] text-[var(--aa-ink-soft)]">
                        <tr>
                            {visibleColumns.map((column) => (
                                <th
                                    key={column}
                                    className="border-b border-[var(--aa-border)] px-3 py-2 font-medium"
                                >
                                    {column}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibleRows.map((row, rowIndex) => (
                            <tr
                                key={`row-${rowIndex}`}
                                className="border-b border-[var(--aa-border)] align-top last:border-b-0"
                            >
                                {visibleColumns.map((column, columnIndex) => (
                                    <td
                                        key={`${rowIndex}-${column}`}
                                        className="px-3 py-2 whitespace-pre-wrap text-[var(--aa-ink)]"
                                    >
                                        {row[columnIndex] ?? ''}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {rows.length > visibleRows.length ||
            columns.length > visibleColumns.length ? (
                <div className="border-t border-[var(--aa-border)] bg-[var(--aa-surface)] px-3 py-2 text-[11px] text-[var(--aa-ink-muted)]">
                    Showing {visibleRows.length.toLocaleString()} of{' '}
                    {rows.length.toLocaleString()} rows and{' '}
                    {visibleColumns.length.toLocaleString()} of{' '}
                    {columns.length.toLocaleString()} columns.
                </div>
            ) : null}
        </div>
    );
}

function truncateJsonString(value: string): string {
    if (value.length <= MAX_JSON_STRING_LENGTH) {
        return value;
    }
    return `${value.slice(0, MAX_JSON_STRING_LENGTH - 1)}…`;
}

function summarizeJsonNode(value: unknown): string {
    if (Array.isArray(value)) {
        return `[${value.length}]`;
    }
    if (isRecord(value)) {
        return `{${Object.keys(value).length}}`;
    }
    if (typeof value === 'string') {
        return `"${truncateJsonString(value)}"`;
    }
    return stringifyPayload(value).text;
}

function JsonTreeNode({
    depth = 0,
    label,
    value,
}: {
    readonly depth?: number;
    readonly label: string | null;
    readonly value: unknown;
}) {
    const isArrayValue = Array.isArray(value);
    const isObjectValue = isRecord(value);

    if (!isArrayValue && !isObjectValue) {
        const content =
            typeof value === 'string'
                ? `"${truncateJsonString(value)}"`
                : summarizeJsonNode(value);
        return (
            <div className="flex gap-2 py-0.5 text-[12px]">
                {label ? (
                    <span className="font-medium text-[var(--aa-ink-soft)]">
                        {label}:
                    </span>
                ) : null}
                <span className="min-w-0 break-words text-[var(--aa-ink)]">
                    {content}
                </span>
            </div>
        );
    }

    const entries = isArrayValue
        ? value.map((child, index) => [`${index}`, child] as const)
        : Object.entries(value);
    const visibleEntries = entries.slice(0, MAX_JSON_TREE_CHILDREN);
    const hiddenCount = Math.max(entries.length - visibleEntries.length, 0);
    const summary = summarizeJsonNode(value);

    return (
        <details open={depth < 2} className="py-0.5">
            <summary className="cursor-pointer text-[12px] font-medium text-[var(--aa-ink-soft)]">
                {label ? `${label}: ` : ''}
                <span className="font-mono text-[var(--aa-ink-muted)]">
                    {summary}
                </span>
            </summary>
            {depth >= MAX_JSON_TREE_DEPTH ? (
                <div className="mt-1 ml-4 text-[11px] text-[var(--aa-ink-faint)]">
                    Collapsed at max depth.
                </div>
            ) : (
                <div className="mt-1 ml-4 border-l border-[var(--aa-border)] pl-3">
                    {visibleEntries.map(([entryLabel, entryValue]) => (
                        <JsonTreeNode
                            key={entryLabel}
                            depth={depth + 1}
                            label={isArrayValue ? null : entryLabel}
                            value={entryValue}
                        />
                    ))}
                    {hiddenCount > 0 ? (
                        <div className="py-0.5 text-[11px] text-[var(--aa-ink-faint)]">
                            +{hiddenCount} more entr
                            {hiddenCount === 1 ? 'y' : 'ies'}
                        </div>
                    ) : null}
                </div>
            )}
        </details>
    );
}

/*
 * Fields that read as a human outcome when hoisted into the summary
 * line. Order matters: earlier keys are more informative.
 */
const SUMMARY_PRIORITY_KEYS = [
    'status',
    'state',
    'outcome',
    'chart_kind',
    'point_count',
    'count',
    'total',
    'row_count',
    'result_count',
    'message',
    'summary',
    'title',
    'name',
] as const;
const MAX_SUMMARY_PARTS = 4;
const MAX_SUMMARY_VALUE_CHARS = 80;

function formatSummaryScalar(value: unknown): string | null {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) {
            return null;
        }
        return trimmed.length > MAX_SUMMARY_VALUE_CHARS
            ? `${trimmed.slice(0, MAX_SUMMARY_VALUE_CHARS - 1)}…`
            : trimmed;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    return null;
}

/**
 * Distills a structured tool payload into one readable line — the
 * "what happened" answer that leads the block, e.g.
 * "status ok · chart_kind line · point_count 18".
 */
export function describeStructuredPayload(value: unknown): string | null {
    if (Array.isArray(value)) {
        return `${value.length.toLocaleString()} item${value.length === 1 ? '' : 's'}`;
    }
    if (!isRecord(value)) {
        return null;
    }
    const parts: Array<string> = [];
    const usedKeys = new Set<string>();
    for (const key of SUMMARY_PRIORITY_KEYS) {
        const formatted = formatSummaryScalar(value[key]);
        if (formatted !== null) {
            parts.push(`${key} ${formatted}`);
            usedKeys.add(key);
        }
        if (parts.length >= MAX_SUMMARY_PARTS) {
            break;
        }
    }
    if (parts.length < MAX_SUMMARY_PARTS) {
        for (const [key, entryValue] of Object.entries(value)) {
            if (usedKeys.has(key)) {
                continue;
            }
            const formatted = formatSummaryScalar(entryValue);
            if (formatted !== null) {
                parts.push(`${key} ${formatted}`);
            }
            if (parts.length >= MAX_SUMMARY_PARTS) {
                break;
            }
        }
    }
    const fieldCount = Object.keys(value).length;
    parts.push(
        `${fieldCount.toLocaleString()} field${fieldCount === 1 ? '' : 's'}`,
    );
    return parts.join(' · ');
}

export function formatConsoleDuration(durationMs: number): string {
    if (durationMs < 1_000) {
        return `${durationMs} ms`;
    }
    return `${(durationMs / 1_000).toFixed(1)} s`;
}

function ConsoleChip({
    label,
    tone = 'neutral',
}: {
    readonly label: string;
    readonly tone?: 'danger' | 'neutral' | 'ok';
}) {
    return (
        <span
            className={cn(
                'aa-mono rounded-[2px] px-1.5 py-0.5 text-[11px] leading-none',
                tone === 'ok' &&
                    'bg-[var(--aa-success-soft)] text-[var(--aa-success)]',
                tone === 'danger' &&
                    'bg-[var(--aa-danger-soft)] text-[var(--aa-danger)]',
                tone === 'neutral' &&
                    'bg-[var(--aa-surface-sunk)] text-[var(--aa-ink-soft)]',
            )}
        >
            {label}
        </span>
    );
}

function ConsoleTextBlock({
    label,
    text,
    tone = 'neutral',
}: {
    readonly label: string | null;
    readonly text: string;
    readonly tone?: 'danger' | 'neutral';
}) {
    const { text: visibleText, omittedChars } = truncateArtifactText(text);
    return (
        <div>
            {label ? (
                <p className="aa-mono mb-1 text-[10px] font-medium tracking-wide text-[var(--aa-ink-faint)] uppercase">
                    {label}
                </p>
            ) : null}
            <pre
                className={cn(
                    'max-h-[24rem] overflow-auto rounded-[2px] px-3 py-2 font-mono text-[12px] leading-5 whitespace-pre-wrap',
                    tone === 'danger'
                        ? 'bg-[var(--aa-danger-soft)] text-[var(--aa-danger)]'
                        : 'bg-[var(--aa-surface-sunk)] text-[var(--aa-ink)]',
                )}
            >
                {visibleText}
            </pre>
            {omittedChars > 0 ? (
                <p className="mt-1 text-[11px] text-[var(--aa-ink-muted)]">
                    Truncated — {omittedChars.toLocaleString()} more characters
                    in the raw payload.
                </p>
            ) : null}
        </div>
    );
}

/**
 * Human-first view of process-style output: outcome chips, then
 * stdout/stderr, with the full payload (hashes, audit refs, rlimits)
 * demoted behind a raw toggle. The search box and line numbers live in
 * the raw view only.
 */
export function ConsoleOutputView({
    stdout,
    stderr,
    exitCode,
    durationMs,
    stepName,
    timedOut,
    outputTruncated,
    raw,
}: {
    readonly stdout: string;
    readonly stderr: string;
    readonly exitCode: number;
    readonly durationMs: number | null;
    readonly stepName: string | null;
    readonly timedOut: boolean;
    readonly outputTruncated: boolean;
    readonly raw: unknown;
}) {
    const hasStdout = stdout.trim().length > 0;
    const hasStderr = stderr.trim().length > 0;
    return (
        <div
            data-testid="admin-tool-output-console"
            className="overflow-hidden rounded-[2px] border border-[var(--aa-border)] bg-white"
        >
            <div className="flex flex-wrap items-center gap-1.5 border-b border-[var(--aa-border)] px-3 py-2">
                <ConsoleChip
                    label={`exit ${exitCode}`}
                    tone={exitCode === 0 ? 'ok' : 'danger'}
                />
                {timedOut ? (
                    <ConsoleChip label="timed out" tone="danger" />
                ) : null}
                {durationMs !== null ? (
                    <ConsoleChip label={formatConsoleDuration(durationMs)} />
                ) : null}
                {stepName ? <ConsoleChip label={`step ${stepName}`} /> : null}
            </div>
            <div className="space-y-2 px-3 py-2">
                {hasStdout ? (
                    <ConsoleTextBlock
                        label={hasStderr ? 'stdout' : null}
                        text={stdout}
                    />
                ) : null}
                {hasStderr ? (
                    <ConsoleTextBlock
                        label="stderr"
                        text={stderr}
                        tone="danger"
                    />
                ) : null}
                {!hasStdout && !hasStderr ? (
                    <p className="text-[12px] text-[var(--aa-ink-muted)]">
                        No output.
                    </p>
                ) : null}
                {outputTruncated ? (
                    <p className="text-[11px] text-[var(--aa-ink-muted)]">
                        Output shown here is truncated; the full text is in the
                        raw payload or linked artifact.
                    </p>
                ) : null}
            </div>
            <details className="border-t border-[var(--aa-border)] bg-[var(--aa-surface)]">
                <summary className="aa-mono cursor-pointer px-3 py-2 text-[11px] font-medium text-[var(--aa-ink-faint)]">
                    Raw payload
                </summary>
                <div className="border-t border-[var(--aa-border)]">
                    <JsonPayloadViewer
                        value={raw}
                        title="Raw payload"
                        filename="admin-agent-payload.json"
                        className="rounded-none border-0"
                        maxVisibleChars={250_000}
                        maxVisibleLines={10_000}
                    />
                </div>
            </details>
        </div>
    );
}

export function JsonRenderer({
    filename,
    value,
    variant,
}: {
    readonly filename: string | null;
    readonly value: unknown;
    readonly variant: AdminToolOutputVariant;
}) {
    const summary = describeStructuredPayload(value);
    return (
        <div
            data-testid="admin-tool-output-json"
            className="overflow-hidden rounded-[2px] border border-[var(--aa-border)] bg-white"
        >
            {summary ? (
                <div
                    data-testid="admin-tool-output-json-summary"
                    className="aa-mono border-b border-[var(--aa-border)] px-3 py-2 text-[12px] text-[var(--aa-ink)]"
                >
                    {summary}
                </div>
            ) : null}
            <details open={variant === 'file'}>
                <summary className="aa-mono cursor-pointer bg-[var(--aa-surface)] px-3 py-2 text-[11px] font-medium text-[var(--aa-ink-soft)]">
                    Payload
                </summary>
                <div className="border-t border-[var(--aa-border)]">
                    <JsonPayloadViewer
                        value={value}
                        title="Payload"
                        filename={filename ?? 'admin-agent-payload.json'}
                        className="rounded-none border-0"
                        maxVisibleChars={250_000}
                        maxVisibleLines={10_000}
                    />
                </div>
            </details>
            <details className="border-t border-[var(--aa-border)] bg-[var(--aa-surface)]">
                <summary className="aa-mono cursor-pointer px-3 py-2 text-[11px] font-medium text-[var(--aa-ink-faint)]">
                    Field tree
                </summary>
                <div className="max-h-[30rem] overflow-auto border-t border-[var(--aa-border)] bg-white px-3 py-2">
                    <JsonTreeNode label={null} value={value} />
                </div>
            </details>
        </div>
    );
}

export function MarkdownRenderer({
    markdown,
    variant,
}: {
    readonly markdown: string;
    readonly variant: AdminToolOutputVariant;
}) {
    const visibleMarkdown =
        variant === 'inline' && markdown.length > MAX_INLINE_MARKDOWN_CHARS
            ? `${markdown.slice(0, MAX_INLINE_MARKDOWN_CHARS - 1)}…`
            : markdown;

    return (
        <div
            data-testid="admin-tool-output-markdown"
            className="rounded-[2px] border border-[var(--aa-border)] bg-white px-3 py-2"
        >
            <Markdown className="max-w-none text-xs leading-6 text-[var(--aa-ink)]">
                {visibleMarkdown}
            </Markdown>
            {visibleMarkdown !== markdown ? (
                <p className="mt-2 text-[11px] text-[var(--aa-ink-muted)]">
                    Truncated long markdown output for performance.
                </p>
            ) : null}
        </div>
    );
}
